/**
 * CampaignMarkdown Component
 *
 * Custom markdown renderer for campaign-related LLM responses.
 * Provides styled rendering with platform icons and better visual hierarchy.
 *
 * Features:
 * - Platform-specific emoji/icon rendering
 * - Custom heading styles with better spacing
 * - Horizontal rule styling
 * - List formatting with proper indentation
 * - Code block syntax highlighting
 *
 * Usage:
 *   <CampaignMarkdown content={assistantMessage} />
 */

import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface CampaignMarkdownProps {
  content: string;
  className?: string;
}

export const CampaignMarkdown: React.FC<CampaignMarkdownProps> = ({
  content,
  className = '',
}) => {
  return (
    <div className={`campaign-markdown ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Headings with better spacing and styling
          h1: ({ node, children, ...props }: any) => (
            <h1
              className="text-2xl font-bold text-ink mb-4 mt-6 first:mt-0"
              {...props}
            >
              {children}
            </h1>
          ),
          h2: ({ node, children, ...props }: any) => (
            <h2
              className="text-xl font-semibold text-ink mb-3 mt-5 first:mt-0"
              {...props}
            >
              {children}
            </h2>
          ),
          h3: ({ node, children, ...props }: any) => (
            <h3
              className="text-lg font-semibold text-ink mb-2 mt-4"
              {...props}
            >
              {children}
            </h3>
          ),
          h4: ({ node, children, ...props }: any) => (
            <h4
              className="text-base font-semibold text-ink-2 mb-2 mt-3"
              {...props}
            >
              {children}
            </h4>
          ),

          // Paragraphs with proper spacing
          p: ({ node, children, ...props }: any) => (
            <p className="text-base leading-relaxed text-ink-2 mb-3" {...props}>
              {children}
            </p>
          ),

          // Lists with better styling
          ul: ({ node, children, ...props }: any) => (
            <ul className="list-none space-y-1.5 mb-4 ml-0" {...props}>
              {children}
            </ul>
          ),
          ol: ({ node, children, ...props }: any) => (
            <ol className="list-decimal list-inside space-y-1.5 mb-4 ml-2" {...props}>
              {children}
            </ol>
          ),
          li: ({ node, children, ...props }: any) => (
            <li className="text-base text-ink-2 leading-relaxed" {...props}>
              {children}
            </li>
          ),

          // Horizontal rules with custom styling
          hr: ({ node, ...props }: any) => (
            <hr className="my-6 border-0 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent" {...props} />
          ),

          // Strong/Bold text
          strong: ({ node, children, ...props }: any) => (
            <strong className="font-semibold text-ink" {...props}>
              {children}
            </strong>
          ),

          // Emphasis/Italic text
          em: ({ node, children, ...props }: any) => (
            <em className="italic text-ink-2" {...props}>
              {children}
            </em>
          ),

          // Code blocks with background
          code: ({ node, inline, children, ...props }: any) => {
            if (inline) {
              return (
                <code
                  className="px-1.5 py-0.5 bg-inset dark:bg-card-darkAlt text-primary dark:text-primary-light rounded text-sm font-mono"
                  {...props}
                >
                  {children}
                </code>
              );
            }
            return (
              <code
                className="block p-3 bg-inset dark:bg-card-darkAlt text-ink rounded-lg text-sm font-mono overflow-x-auto mb-4"
                {...props}
              >
                {children}
              </code>
            );
          },

          // Blockquotes
          blockquote: ({ node, children, ...props }: any) => (
            <blockquote
              className="border-l-4 border-primary dark:border-primary-light pl-4 py-2 my-4 bg-ground rounded-r"
              {...props}
            >
              {children}
            </blockquote>
          ),

          // Links
          a: ({ node, children, ...props }: any) => (
            <a
              className="text-primary dark:text-primary-light hover:underline font-medium"
              target="_blank"
              rel="noopener noreferrer"
              {...props}
            >
              {children}
            </a>
          ),

          // Tables
          table: ({ node, children, ...props }: any) => (
            <div className="overflow-x-auto mb-4">
              <table className="min-w-full divide-y divide-line" {...props}>
                {children}
              </table>
            </div>
          ),
          thead: ({ node, children, ...props }: any) => (
            <thead className="bg-ground dark:bg-card-darkAlt" {...props}>
              {children}
            </thead>
          ),
          tbody: ({ node, children, ...props }: any) => (
            <tbody className="bg-card dark:bg-background-dark divide-y divide-line" {...props}>
              {children}
            </tbody>
          ),
          tr: ({ node, children, ...props }: any) => (
            <tr {...props}>
              {children}
            </tr>
          ),
          th: ({ node, children, ...props }: any) => (
            <th
              className="px-4 py-3 text-left text-xs font-medium text-ink-3 uppercase tracking-wider"
              {...props}
            >
              {children}
            </th>
          ),
          td: ({ node, children, ...props }: any) => (
            <td className="px-4 py-3 text-sm text-ink-2" {...props}>
              {children}
            </td>
          ),
        }}
      >
        {content}
      </ReactMarkdown>

      <style>{`
        .campaign-markdown {
          line-height: 1.6;
        }

        /* Ensure first element has no top margin */
        .campaign-markdown > :first-child {
          margin-top: 0 !important;
        }

        /* Ensure last element has no bottom margin */
        .campaign-markdown > :last-child {
          margin-bottom: 0 !important;
        }

        /* Platform emoji spacing */
        .campaign-markdown p:has(> strong:first-child) {
          margin-bottom: 0.5rem;
        }
      `}</style>
    </div>
  );
};

export default CampaignMarkdown;
